import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Play, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import PublicNav from "../components/PublicNav";
import SeoExpandable from "../components/SeoExpandable";
import TickerMarquee from "../components/TickerMarquee";
import { getMarketWindowForNow } from "../utils/marketStatus";
import { isAppWebView } from "../utils/appWebView";
import { openExternalLink } from "../utils/appInteractions";

// ─── Brand Colors (BharatMatka style) ───────────────────────────────────────
const ORANGE = "#f97316";
const GREEN  = "#16a34a";
const PURPLE = "#312e81";

// ─── Root Component ──────────────────────────────────────────────────────────
function Home() {
  const navigate = useNavigate();
  const [settings, setSettings]             = useState({});
  const [markets, setMarkets]               = useState([]);
  const [normalRateMap, setNormalRateMap]   = useState({});
  const [liveTime, setLiveTime]             = useState(() => fmtLiveTime(new Date()));
  const [refreshing, setRefreshing]         = useState(false);
  const [vw, setVw]                         = useState(() => window.innerWidth);
  const [openFaq, setOpenFaq]               = useState(null);

  const isMobile  = vw < 768;
  const isTablet  = vw >= 768 && vw < 1024;
  const inWebView = isAppWebView();

  // ── helpers ─────────────────────────────────────────────────────────────────
  const downloadUrl   = useMemo(() => String(settings.app_download_url   || "").trim(), [settings.app_download_url]);
  const downloadLabel = useMemo(() => String(settings.app_download_label || "").trim() || "Download App", [settings.app_download_label]);
  const tickerText    = useMemo(() => String(settings.home_running_notice || "Auto Deposit | Auto Withdraw | Fast Results | Secure Wallet | Play Online Satta Matka").trim(), [settings.home_running_notice]);

  const displayMarkets = useMemo(() =>
    markets.map((item) => {
      const win       = getMarketWindowForNow(item);
      const openT     = win.open_time  || item.open_time;
      const closeT    = win.close_time || item.close_time;
      const nowM      = new Date().getHours() * 60 + new Date().getMinutes();
      const openM     = toMins(openT);
      const closeM    = toMins(closeT);
      const isOpen    = openM >= 0 && closeM > openM && nowM >= openM && nowM < closeM;
      return { id: item.id, name: item.name, result: item.result || "***-**-***", openTime: fmtTime(openT), closeTime: fmtTime(closeT), isOpen };
    }),
    [markets]
  );

  const displayRates = useMemo(() => RATE_META.map((m) => ({ ...m, val: Number(normalRateMap?.[m.key] || 0) })), [normalRateMap]);

  // ── data loading ─────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [mR, rR, sR] = await Promise.allSettled([
        API.get("/markets"),
        API.get("/game-rates?market_type=normal"),
        API.get("/settings/public"),
      ]);
      if (mR.status === "fulfilled") {
        setMarkets(
          (Array.isArray(mR.value.data) ? mR.value.data : [])
            .filter((i) => (i.market_type || "normal") !== "starline")
            .sort((a, b) => toMins(a.open_time) - toMins(b.open_time))
        );
      }
      if (rR.status === "fulfilled") setNormalRateMap(rR.value.data || {});
      if (sR.status === "fulfilled") setSettings(sR.value.data || {});
      setLiveTime(fmtLiveTime(new Date()));
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(() => setLiveTime(fmtLiveTime(new Date())), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { const h = () => setVw(window.innerWidth); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);

  // ── actions ──────────────────────────────────────────────────────────────────
  const goPlay     = ()     => navigate("/login");
  const goMktChart = (id, v)=> id ? navigate(`/charts/market/${id}?view=${v}`) : navigate("/charts");
  const doDownload = ()     => { if (downloadUrl) openExternalLink(downloadUrl); else alert("Download link coming soon"); };

  const marketList = displayMarkets.length ? displayMarkets : FALLBACK_MARKETS;

  // ── grid columns ─────────────────────────────────────────────────────────────
  const mktCols = isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)";

  return (
    <div style={pg}>

      {/* ── NAVIGATION ───────────────────────────────────────────────────────── */}
      <PublicNav />

      {/* ── NOTICE TICKER ────────────────────────────────────────────────────── */}
      <div style={ticker}>
        <TickerMarquee
          text={tickerText}
          speed={55}
          outerStyle={tickerOuter}
          trackStyle={tickerTrack}
          textStyle={tickerTxt}
        />
      </div>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section style={{ ...heroWrap, flexDirection: isMobile ? "column" : "row" }}>
        {/* Left */}
        <div style={heroLeft}>
          <p style={heroUrl}>www.playonlinesattamatka.com</p>
          <h1 style={heroH1}>
            <span style={{ color: ORANGE }}>Play Online</span>
            <br />
            <span style={{ color: GREEN }}>Satta Matka</span>
            <br />
            <span style={{ color: "#fff", fontSize: "clamp(13px,2.5vw,18px)", fontWeight: 700, opacity: 0.85 }}>Live Results, Charts &amp; Game Rates</span>
          </h1>
          <p style={heroTagline}>Best Rates • Fast Results • Secure Wallet</p>
          <div style={liveBadge}>🟢 Live — Updated {liveTime}</div>
          <div style={heroActions}>
            <button style={bigPlayBtn} onClick={goPlay}>
              <Play size={18} fill="#fff" color="#fff" />
              Play Now
            </button>
            {!inWebView && downloadUrl && (
              <button style={bigDlBtn} onClick={doDownload}>{downloadLabel}</button>
            )}
          </div>
          <p style={ageNote}>18+ Only • Play Responsibly</p>
        </div>

        {/* Right — Game Win Rates card */}
        <div style={{ ...heroRight, width: isMobile ? "100%" : 320 }}>
          <div style={rateCard}>
            <div style={rateCardHdr}>
              <span>🎮 Game Win Rates</span>
              <span style={rateCardBadge}>LIVE</span>
            </div>
            {displayRates.map((r) => (
              <div key={r.key} style={rateRow}>
                <span style={{ ...rateBadge, background: r.color }}>{r.badge}</span>
                <span style={rateName}>{r.title}</span>
                <span style={rateAmt}>{fmtRateRupee(r.val)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AVAILABLE GAMES / MARKETS ─────────────────────────────────────────── */}
      <section style={section}>
        <div style={secHead}>
          <div>
            <h2 style={secTitle}>Available Games</h2>
            <p style={secSub}>We have multiple types of games for you</p>
          </div>
          <button style={refreshBtnStyle} onClick={load} disabled={refreshing}>
            <RefreshCw size={14} />
            {refreshing ? "Updating..." : "Refresh"}
          </button>
        </div>

        <div style={{ ...mktGrid, gridTemplateColumns: mktCols }}>
          {marketList.map((m) => (
            <div key={m.id || m.name} style={mktCard}>
              {/* Top row: name + status */}
              <div style={mktTopRow}>
                <span style={mktName}>{m.name}</span>
                <span style={{ ...statusPill, background: m.isOpen ? "#dcfce7" : "#fee2e2", color: m.isOpen ? "#15803d" : "#dc2626" }}>
                  {m.isOpen ? "Open" : "Closed"}
                </span>
              </div>

              {/* Time */}
              <div style={mktTime}>{m.openTime} — {m.closeTime}</div>

              {/* Result */}
              <div style={mktResult}>{m.result}</div>

              {/* Chart links + Play Now */}
              <div style={mktFoot}>
                <div style={chartRow}>
                  <button style={chartLnk} onClick={(e) => { e.stopPropagation(); goMktChart(m.id, "jodi"); }}>
                    Jodi Chart
                  </button>
                  <span style={divider}>|</span>
                  <button style={chartLnk} onClick={(e) => { e.stopPropagation(); goMktChart(m.id, "panel"); }}>
                    Panna Chart
                  </button>
                </div>
                <button style={playNowBtn} onClick={goPlay}>
                  Play Now <Play size={13} fill="#fff" color="#fff" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── APP DOWNLOAD STRIP ────────────────────────────────────────────────── */}
      {!inWebView && downloadUrl && (
        <div style={dlStrip}>
          <img src="/Google_Play_2016_icon.svg" alt="" style={{ width: 28, height: 28 }} />
          <strong style={{ fontSize: 16, fontWeight: 900 }}>{downloadLabel}</strong>
          <button style={dlBtn} onClick={doDownload}>Download Now</button>
        </div>
      )}

      {/* ── WHY CHOOSE US ────────────────────────────────────────────────────── */}
      <section style={{ ...seoSection, padding: isMobile ? "32px 16px" : "48px 24px" }} aria-labelledby="why-choose-heading">
        <div style={seoInner}>
          <p style={seoCategoryTag}>Trusted Platform</p>
          <h2 id="why-choose-heading" style={seoH2}>Why Choose Play Online Satta Matka?</h2>
          <p style={seoLead}>Lakhs of players across India trust us for the best matka rates, instant wallet, and live results every day.</p>
          <div style={{ ...featGrid, gridTemplateColumns: isMobile ? "1fr 1fr" : isTablet ? "1fr 1fr 1fr" : "repeat(3,1fr)" }}>
            {HOME_FEATURES.map((f) => (
              <article key={f.title} style={featCard}>
                <div style={featIcon}>{f.icon}</div>
                <h3 style={featTitle}>{f.title}</h3>
                <p style={featBody}>{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT IS SATTA MATKA ───────────────────────────────────────────────── */}
      <section style={{ ...seoSection, background: "#fff7ed", padding: isMobile ? "32px 16px" : "48px 24px" }} aria-labelledby="what-is-heading">
        <div style={seoInner}>
          <p style={seoCategoryTag}>Learn</p>
          <h2 id="what-is-heading" style={seoH2}>What is Online Satta Matka?</h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 380px", gap: isMobile ? 20 : 32, alignItems: "start" }}>
            <div>
              <p style={seoBody}>
                <strong>Satta Matka</strong> is one of India's most popular number-based games of chance. Originally started in Mumbai, Matka has evolved into a fully digital experience where players can bet on numbers, view live results, track charts, and manage their wallet — all from their phone.
              </p>
              <p style={seoBody}>
                Every Matka market has an <strong>Open</strong> and <strong>Close</strong> session. Players choose numbers like Single Digit (0–9), Jodi (00–99), or Patti (a 3-digit combination). Results are declared at the market closing time and winners receive payouts based on the rates.
              </p>
              <p style={seoBody}>
                Popular markets include <strong>Kalyan Matka</strong>, <strong>Milan Day/Night</strong>, <strong>Rajdhani Day/Night</strong>, <strong>Main Bazar</strong>, and Starline markets. Each market has different timings and win-rate multipliers.
              </p>
            </div>
            <div>
              <div style={rateInfoBox}>
                <div style={rateInfoHead}>Game Win Rates</div>
                {[
                  ["Single Digit (0–9)", normalRateMap.single],
                  ["Jodi (00–99)",        normalRateMap.jodi],
                  ["Single Patti",        normalRateMap.sp],
                  ["Double Patti",        normalRateMap.dp],
                  ["Triple Patti",        normalRateMap.tp],
                  ["Half Sangam",         normalRateMap.half_sangam],
                  ["Full Sangam",         normalRateMap.full_sangam],
                ].map(([name, rate]) => (
                  <div key={name} style={rateInfoRow}>
                    <span style={rateInfoName}>{name}</span>
                    <span style={rateInfoVal}>{rate ? `${rate}× your bet` : "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={seoCtaRow}>
            <button style={seoPrimaryBtn} onClick={() => navigate("/how-to-play")}>How to Play Guide →</button>
            <button style={seoSecondaryBtn} onClick={() => navigate("/charts")}>View Matka Charts →</button>
          </div>
        </div>
      </section>

      {/* ── HOW TO PLAY STEPS ────────────────────────────────────────────────── */}
      <section style={{ ...seoSection, padding: isMobile ? "32px 16px" : "48px 24px" }} aria-labelledby="how-to-play-heading">
        <div style={seoInner}>
          <p style={seoCategoryTag}>Quick Start</p>
          <h2 id="how-to-play-heading" style={seoH2}>How to Play Matka Online — 4 Easy Steps</h2>
          <div style={{ ...stepsGrid, gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)" }}>
            {HOW_STEPS.map((s, i) => (
              <div key={s.title} style={stepCard}>
                <div style={stepNum}>{i + 1}</div>
                <h3 style={stepTitle}>{s.title}</h3>
                <p style={stepDesc}>{s.desc}</p>
              </div>
            ))}
          </div>
          <div style={seoCtaRow}>
            <button style={seoPrimaryBtn} onClick={() => navigate("/login")}>Register Free Now →</button>
            <button style={seoSecondaryBtn} onClick={() => navigate("/game-rates")}>See All Game Rates →</button>
          </div>
        </div>
      </section>

      {/* ── CUSTOMER REVIEWS ──────────────────────────────────────────────────── */}
      <section style={{ ...seoSection, background: "#f0fdf4", padding: isMobile ? "32px 16px" : "48px 24px" }} aria-labelledby="reviews-heading">
        <div style={seoInner}>
          <p style={seoCategoryTag}>Reviews & Rating</p>
          <h2 id="reviews-heading" style={seoH2}>What Our Players Say</h2>
          <div style={ratingBig}>
            <span style={ratingScore}>4.8</span>
            <div>
              <div style={starsRow}>{"★★★★★"}</div>
              <p style={ratingNote}>Based on 12,000+ player reviews</p>
            </div>
          </div>
          <div style={{ ...reviewGrid, gridTemplateColumns: isMobile ? "1fr" : "repeat(2,1fr)" }}>
            {HOME_REVIEWS.map((r) => (
              <article key={r.name} style={reviewCard}>
                <div style={reviewTop}>
                  <div style={{ ...reviewAvatar, background: r.color }}>{r.name[0]}</div>
                  <div>
                    <strong style={reviewName}>{r.name}</strong>
                    <div style={reviewStars}>{"★".repeat(r.stars)}</div>
                  </div>
                  <span style={verifiedBadge}>✓ Verified</span>
                </div>
                <p style={reviewText}>{r.text}</p>
                <span style={reviewDate}>{r.date}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────────── */}
      <section style={{ ...seoSection, padding: isMobile ? "32px 16px" : "48px 24px" }} aria-labelledby="faq-heading">
        <div style={seoInner}>
          <p style={seoCategoryTag}>Help & Support</p>
          <h2 id="faq-heading" style={seoH2}>Frequently Asked Questions</h2>
          <div style={faqList}>
            {HOME_FAQS.map((faq, i) => (
              <div key={faq.q} style={faqItem}>
                <button
                  type="button"
                  style={faqQuestion}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span>{faq.q}</span>
                  <span style={faqChevron}>{openFaq === i ? "−" : "+"}</span>
                </button>
                {openFaq === i && <p style={faqAnswer}>{faq.a}</p>}
              </div>
            ))}
          </div>
          <div style={seoCtaRow}>
            <button style={seoSecondaryBtn} onClick={() => navigate("/faq")}>View All FAQs →</button>
          </div>
        </div>
      </section>

      {/* ── SEO CONTENT ─────────────────────────────────────────────────────── */}
      <SeoExpandable title="Understanding Matka, Teen Patti, and Online Gambling: An Informational Guide" sections={HOME_SEO_SECTIONS} faqs={HOME_SEO_FAQS} />

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer style={ftr}>
        <div style={{ marginBottom: 14 }}><BrandText light /></div>
        <p style={ftrNote}>Players must be 18 years or above to register. Play Responsibly. Underage gaming is prohibited.</p>
        <div style={ftrLinks}>
          {[
            ["Home",                "/"],
            ["How to Play",         "/how-to-play"],
            ["Matka Charts",        "/charts"],
            ["Blog",                "/blog"],
            ["FAQ",                 "/faq"],
            ["About Us",            "/about-us"],
            ["Privacy Policy",      "/privacy-policy"],
            ["Terms & Conditions",  "/terms-and-conditions"],
            ["Refund Policy",       "/refund-policy"],
            ["Responsible Gaming",  "/responsible-gaming"],
            ["Contact Us",          "/contact-us"],
          ].map(([lbl, path]) => (
            <button key={path} style={ftrLnk} onClick={() => navigate(path)}>{lbl}</button>
          ))}
        </div>
        <div style={ftrDivider} />
        <p style={ftrCopy}>© 2026 Play Online Satta Matka. All Rights Reserved.</p>
      </footer>
    </div>
  );
}

// ─── Brand Text ──────────────────────────────────────────────────────────────
function BrandText({ small = false, light = false }) {
  const base = { fontFamily: "'Arial Black', Impact, sans-serif", fontWeight: 900, lineHeight: 1, display: "inline-flex", alignItems: "center", gap: 0 };
  const sz   = small ? 20 : 24;
  return (
    <span style={{ ...base, fontSize: sz }}>
      <span style={{ color: light ? "#fbbf24" : ORANGE }}>Play Online&nbsp;</span>
      <span style={{ color: light ? "#86efac" : GREEN  }}>Satta Matka</span>
    </span>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmtTime(v) {
  if (!v) return "--:--";
  const [h, m = "00"] = String(v).split(":");
  const hr = Number(h);
  return `${String(((hr + 11) % 12) + 1).padStart(2, "0")}:${m} ${hr >= 12 ? "PM" : "AM"}`;
}

function fmtLiveTime(d) {
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

function toMins(v) {
  const m = String(v || "").match(/(\d{1,2}):(\d{2})/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : -1;
}

function fmtRateRupee(val) {
  const n = Number(val || 0);
  if (!n) return "—";
  const p = n * 10;
  return `₹${Number.isInteger(p) ? p : p.toFixed(1)}`;
}

// ─── Constants ───────────────────────────────────────────────────────────────
const RATE_META = [
  { key: "single",       title: "Single Digit",  badge: "SD", color: "#ec4899" },
  { key: "jodi",         title: "Jodi Digit",    badge: "JD", color: "#3b82f6" },
  { key: "red_bracket",  title: "Red Bracket",   badge: "RB", color: "#f97316" },
  { key: "sp",           title: "Single Pana",   badge: "SP", color: "#22c55e" },
  { key: "dp",           title: "Double Pana",   badge: "DP", color: "#a855f7" },
  { key: "tp",           title: "Triple Pana",   badge: "TP", color: "#1d4ed8" },
  { key: "half_sangam",  title: "Half Sangam",   badge: "HS", color: "#0d9488" },
  { key: "full_sangam",  title: "Full Sangam",   badge: "FS", color: "#7c3aed" },
];

const FALLBACK_MARKETS = [
  { id: 1, name: "MILAN MORNING",  result: "***-**-***", openTime: "10:10 AM", closeTime: "11:11 AM", isOpen: false },
  { id: 2, name: "SRIDEVI",        result: "***-**-***", openTime: "11:25 AM", closeTime: "12:25 PM", isOpen: false },
  { id: 3, name: "TIME BAZAR",     result: "***-**-***", openTime: "12:45 PM", closeTime: "01:45 PM", isOpen: false },
  { id: 4, name: "RAJDHANI DAY",   result: "***-**-***", openTime: "03:25 PM", closeTime: "05:25 PM", isOpen: false },
  { id: 5, name: "KALYAN",         result: "***-**-***", openTime: "03:45 PM", closeTime: "05:45 PM", isOpen: false },
  { id: 6, name: "MAIN BAZAR",     result: "***-**-***", openTime: "09:30 PM", closeTime: "12:02 AM", isOpen: false },
];

// ─── Styles ──────────────────────────────────────────────────────────────────
const pg          = { minHeight: "100vh", background: "#f9fafb", fontFamily: "Arial, Helvetica, sans-serif", color: "#111827" };


// ticker
const ticker      = { background: "#fff7ed", borderBottom: "1px solid #fed7aa" };
const tickerOuter = { width: "100%", minHeight: 36, overflow: "hidden" };
const tickerTrack = { display: "inline-flex", alignItems: "center", whiteSpace: "nowrap", willChange: "transform" };
const tickerTxt   = { display: "inline-flex", alignItems: "center", minHeight: 36, padding: "0 30px", color: "#c2410c", fontSize: 13, fontWeight: 700, letterSpacing: "0.02em" };

// hero
const heroWrap    = { display: "flex", alignItems: "flex-start", gap: 28, padding: "36px 24px 32px", maxWidth: 1200, margin: "0 auto", flexWrap: "wrap", background: "#fff", borderBottom: "1px solid #e5e7eb" };
const heroLeft    = { flex: "1 1 300px", minWidth: 0 };
const heroRight   = { flex: "0 1 300px" };
const heroUrl     = { margin: "0 0 6px", fontSize: 13, color: "#6b7280", fontWeight: 600 };
const heroH1      = { margin: "0 0 10px", fontSize: "clamp(28px,5vw,46px)", fontWeight: 900, lineHeight: 1.1 };
const heroTagline = { margin: "0 0 12px", fontSize: 15, color: "#374151" };
const liveBadge   = { display: "inline-flex", padding: "5px 14px", borderRadius: 999, background: "#f0fdf4", color: "#15803d", fontSize: 13, fontWeight: 700, border: "1px solid #bbf7d0", marginBottom: 18 };
const heroActions = { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 };
const bigPlayBtn  = { height: 48, padding: "0 26px", borderRadius: 10, border: 0, background: ORANGE, color: "#fff", fontWeight: 800, fontSize: 16, display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", boxShadow: "0 4px 14px rgba(249,115,22,.35)" };
const bigDlBtn    = { height: 48, padding: "0 22px", borderRadius: 10, border: "2px solid " + PURPLE, background: "#fff", color: PURPLE, fontWeight: 800, fontSize: 14, cursor: "pointer" };
const ageNote     = { margin: 0, fontSize: 12, color: "#9ca3af" };

// rates card (hero right)
const rateCard    = { border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden", background: "#fff", boxShadow: "0 6px 20px rgba(0,0,0,.09)" };
const rateCardHdr = { background: PURPLE, color: "#fff", padding: "11px 16px", fontWeight: 800, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "space-between" };
const rateCardBadge = { fontSize: 11, fontWeight: 900, background: "#f97316", color: "#fff", padding: "2px 8px", borderRadius: 999 };
const rateRow     = { display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderBottom: "1px solid #f3f4f6" };
const rateBadge   = { width: 30, height: 22, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 900, flexShrink: 0 };
const rateName    = { flex: 1, fontSize: 13, color: "#111827" };
const rateAmt     = { fontSize: 13, fontWeight: 800, color: PURPLE };

// markets section
const section     = { padding: "32px 24px", maxWidth: 1200, margin: "0 auto" };
const secHead     = { display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" };
const secTitle    = { margin: 0, fontSize: "clamp(20px,3vw,26px)", fontWeight: 900, color: PURPLE };
const secSub      = { margin: "4px 0 0", color: "#6b7280", fontSize: 14 };
const refreshBtnStyle = { height: 36, padding: "0 14px", borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 700, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" };
const mktGrid     = { display: "grid", gap: 14 };
const mktCard     = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "16px 18px", boxShadow: "0 2px 8px rgba(0,0,0,.04)" };
const mktTopRow   = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 };
const mktName     = { fontSize: 16, fontWeight: 900, color: "#111827", textTransform: "uppercase", letterSpacing: "0.04em" };
const statusPill  = { fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 999 };
const mktTime     = { fontSize: 12, color: "#6b7280", marginBottom: 6 };
const mktResult   = { fontSize: 22, fontWeight: 900, color: "#111827", letterSpacing: "0.06em", marginBottom: 10 };
const mktFoot     = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" };
const chartRow    = { display: "inline-flex", alignItems: "center", gap: 5 };
const chartLnk    = { border: 0, background: "transparent", padding: 0, color: ORANGE, fontSize: 12, fontWeight: 700, cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" };
const divider     = { color: "#d1d5db", fontSize: 12 };
const playNowBtn  = { height: 38, padding: "0 16px", borderRadius: 8, border: 0, background: PURPLE, color: "#fff", fontWeight: 800, fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", whiteSpace: "nowrap" };

// download strip
const dlStrip     = { background: GREEN, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 14, padding: "18px 24px", flexWrap: "wrap" };
const dlBtn       = { height: 40, padding: "0 20px", borderRadius: 8, border: "2px solid #fff", background: "transparent", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" };

// ─── SEO Section Data ────────────────────────────────────────────────────────
const HOME_FEATURES = [
  { icon: "💰", title: "Full Game Rates Guaranteed", desc: "Single 9×, Jodi 90×, Single Patti 150×, Double Patti 270×, Triple Patti 800×. Best rates in India — guaranteed every session." },
  { icon: "⚡", title: "Instant Deposit & Withdrawal", desc: "UPI, PhonePe, Google Pay — deposits credited in seconds. Withdrawals processed daily. Fast, secure and verified every time." },
  { icon: "📊", title: "Live Market Results", desc: "Kalyan, Milan, Rajdhani, Main Bazar and Starline results updated live. Full Jodi and Panel chart history available anytime." },
  { icon: "🔒", title: "Secure OTP Login", desc: "No password needed. Your account is protected by mobile OTP every time. Your wallet and bids are always safe with us." },
  { icon: "🎮", title: "8 Game Types", desc: "Single Digit, Jodi, Red Bracket, Single Patti, Double Patti, Triple Patti, Half Sangam and Full Sangam — all in one platform." },
  { icon: "📱", title: "Works on All Devices", desc: "Play on Android, iPhone, tablet or desktop browser. Download the free app or play directly on the website — your choice." },
];

const HOW_STEPS = [
  { title: "Register Free", desc: "Enter your mobile number, verify with OTP and create your account in under 30 seconds. No documents needed." },
  { title: "Add Money to Wallet", desc: "Deposit using UPI, PhonePe or Google Pay. Minimum deposit is low and credits are instant after verification." },
  { title: "Choose Your Market", desc: "Pick from Kalyan, Milan, Rajdhani, Main Bazar or Starline markets. Check open/close times and current status." },
  { title: "Place Your Bid & Win", desc: "Choose game type (Single, Jodi, Patti), enter your number and amount. Win up to 800× your bet when results are declared!" },
];

const HOME_REVIEWS = [
  { name: "Rajesh Bhatia", color: "#7c3aed", stars: 5, text: "Best matka platform I have used. Withdrawal done in same day. Full rates given — 150× on single patti. Very trusted app.", date: "10 Jul, 2026" },
  { name: "Suresh Kumar", color: "#059669", stars: 5, text: "Kalyan and Milan results update instantly. Jodi chart is very clear. Deposit from PhonePe works in seconds. Highly recommended!", date: "9 Jul, 2026" },
  { name: "Priya Sharma", color: "#ea580c", stars: 5, text: "OTP login is very safe. My wallet is always protected. Customer support helped me with withdrawal. 5 stars without doubt.", date: "8 Jul, 2026" },
  { name: "Ankit Verma", color: "#2563eb", stars: 5, text: "Playing here for 6 months. Main Bazar result always on time. App works fast on my Android. Best rates in market — triple patti 800×!", date: "7 Jul, 2026" },
];

const HOME_FAQS = [
  { q: "What is Satta Matka?", a: "Satta Matka is a popular number-based game of chance in India. Players bet on numbers (Single Digit, Jodi, or Patti) and win based on the declared result. Markets like Kalyan, Milan, and Rajdhani operate at specific times daily." },
  { q: "How do I play online Matka on this website?", a: "Register with your mobile number, verify via OTP, add money to your wallet via UPI, choose a market, select a game type (Single, Jodi, Patti etc.), enter your number and bet amount, then wait for results." },
  { q: "What are the game rates on Play Online Satta Matka?", a: "Single Digit wins 9× your bet, Jodi wins 90×, Single Patti wins 150×, Double Patti wins 270×, Triple Patti wins 800×, Half Sangam wins 1500× and Full Sangam wins 11250×. These are full market rates." },
  { q: "How do I deposit money?", a: "Go to Wallet → Add Money. Enter the amount and scan the UPI QR code or use the UPI ID shown. Pay via PhonePe, Google Pay, Paytm or any UPI app. Upload the payment screenshot and your balance is credited after verification." },
  { q: "How long does withdrawal take?", a: "Withdrawal requests are processed within the same day during business hours. Add your bank account or UPI details in your profile first. Minimum withdrawal amount is shown on the withdrawal page." },
  { q: "What is a Jodi in Satta Matka?", a: "Jodi means a pair of two digits (00 to 99). If you bet on Jodi 45 and the market result shows 45 as the Jodi (formed from open and close digits), you win 90 times your bet amount." },
  { q: "What is the difference between Single Patti and Double Patti?", a: "Single Patti (SP) is a 3-digit number where all digits are different (e.g., 123). Double Patti (DP) has two same digits (e.g., 112). Triple Patti (TP) has all three digits same (e.g., 111). SP wins 150×, DP wins 270×, TP wins 800×." },
  { q: "Is this platform safe to use?", a: "Yes. Play Online Satta Matka uses OTP-based login (no stored passwords), encrypted wallet transactions and verified payment processing. Your personal data is never sold. We follow responsible gaming guidelines — players must be 18+." },
];

// ─── SEO Styles ──────────────────────────────────────────────────────────────
const seoSection  = { padding: "48px 24px", background: "#fff" };
const seoInner    = { maxWidth: 1200, margin: "0 auto" };
const seoCategoryTag = { margin: "0 0 8px", fontSize: 13, fontWeight: 800, color: ORANGE, textTransform: "uppercase", letterSpacing: "0.08em" };
const seoH2       = { margin: "0 0 12px", fontSize: "clamp(22px,3.5vw,32px)", fontWeight: 900, color: "#111827" };
const seoLead     = { margin: "0 0 28px", fontSize: 16, color: "#374151", maxWidth: 700 };
const seoBody     = { margin: "0 0 14px", fontSize: 15, color: "#374151", lineHeight: 1.7 };
const seoTwoCol   = { display: "grid", gridTemplateColumns: "1fr 380px", gap: 32, alignItems: "start", flexWrap: "wrap" };
const seoCtaRow   = { marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap" };
const seoPrimaryBtn  = { height: 46, padding: "0 24px", borderRadius: 10, border: 0, background: ORANGE, color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer" };
const seoSecondaryBtn = { height: 46, padding: "0 22px", borderRadius: 10, border: `2px solid ${PURPLE}`, background: "#fff", color: PURPLE, fontWeight: 800, fontSize: 15, cursor: "pointer" };

// features
const featGrid    = { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 18, marginTop: 8 };
const featCard    = { background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 14, padding: "22px 18px" };
const featIcon    = { fontSize: 28, marginBottom: 10 };
const featTitle   = { margin: "0 0 6px", fontSize: 15, fontWeight: 800, color: "#111827" };
const featBody    = { margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.6 };

// rate info box
const rateInfoBox   = { border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden", background: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,.07)" };
const rateInfoHead  = { background: PURPLE, color: "#fff", padding: "12px 16px", fontWeight: 800, fontSize: 14 };
const rateInfoRow   = { display: "flex", justifyContent: "space-between", padding: "9px 16px", borderBottom: "1px solid #f3f4f6", fontSize: 13 };
const rateInfoName  = { color: "#374151" };
const rateInfoVal   = { fontWeight: 800, color: ORANGE };

// steps
const stepsGrid   = { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginTop: 8 };
const stepCard    = { background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 14, padding: "20px 16px", position: "relative" };
const stepNum     = { width: 36, height: 36, borderRadius: "50%", background: PURPLE, color: "#fff", display: "grid", placeItems: "center", fontWeight: 900, fontSize: 16, marginBottom: 12 };
const stepTitle   = { margin: "0 0 6px", fontSize: 15, fontWeight: 800, color: "#111827" };
const stepDesc    = { margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.6 };

// reviews
const ratingBig   = { display: "flex", alignItems: "center", gap: 20, marginBottom: 28, flexWrap: "wrap" };
const ratingScore = { fontSize: 56, fontWeight: 900, color: "#111827", lineHeight: 1 };
const starsRow    = { fontSize: 24, color: "#f59e0b", letterSpacing: 3 };
const ratingNote  = { margin: "4px 0 0", fontSize: 13, color: "#6b7280" };
const reviewGrid  = { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 16 };
const reviewCard  = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "18px 18px 14px" };
const reviewTop   = { display: "flex", alignItems: "center", gap: 12, marginBottom: 10 };
const reviewAvatar = { width: 42, height: 42, borderRadius: "50%", color: "#fff", display: "grid", placeItems: "center", fontWeight: 900, fontSize: 16, flexShrink: 0 };
const reviewName  = { fontSize: 14, fontWeight: 800, color: "#111827" };
const reviewStars = { fontSize: 13, color: "#f59e0b", letterSpacing: 2 };
const verifiedBadge = { marginLeft: "auto", background: "#dcfce7", color: "#15803d", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, flexShrink: 0 };
const reviewText  = { margin: "0 0 10px", fontSize: 13, color: "#374151", lineHeight: 1.6 };
const reviewDate  = { fontSize: 11, color: "#9ca3af" };

// faq
const faqList     = { display: "grid", gap: 10, marginTop: 8 };
const faqItem     = { border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", background: "#fff" };
const faqQuestion = { width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", border: 0, background: "transparent", textAlign: "left", fontWeight: 700, fontSize: 15, color: "#111827", cursor: "pointer", gap: 12 };
const faqChevron  = { fontSize: 20, fontWeight: 900, color: ORANGE, flexShrink: 0 };
const faqAnswer   = { margin: 0, padding: "0 18px 16px", fontSize: 14, color: "#374151", lineHeight: 1.7 };

// footer
const ftr         = { background: "#111827", color: "#fff", padding: "32px 24px", textAlign: "center" };
const ftrNote     = { fontSize: 13, color: "#9ca3af", marginBottom: 16, maxWidth: 600, margin: "8px auto 16px" };
const ftrLinks    = { display: "flex", flexWrap: "wrap", gap: "8px 16px", justifyContent: "center", marginBottom: 18 };
const ftrLnk      = { border: 0, background: "transparent", color: "#d1d5db", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, textDecoration: "none" };
const ftrDivider  = { width: "100%", height: 1, background: "rgba(255,255,255,.12)", margin: "4px 0 12px" };
const ftrCopy     = { fontSize: 12, color: "#6b7280", margin: 0 };

const HOME_SEO_SECTIONS = [
  { heading: `What Is Matka?`, body: `Matka is a number-based gambling format that originated in India several decades ago and has become widely recognized due to its historical significance. The game traces its roots to a time when participants placed bets on the opening and closing rates of cotton traded on the New York Cotton Exchange. When that system ended, it gradually evolved into different number-draw formats that came to be collectively known as &quot;Matka.&quot;

Today, the term Matka refers to a variety of number-based gambling formats that may differ in their rules, draw methods, and gameplay depending on the operator and the region where they are offered. Over the years, several well-known names and markets have become associated with Matka, each following its own specific structure and schedule.

Although Matka has a long cultural history in India, it is important to understand that it is a form of gambling involving financial risk. Outcomes are determined by chance, and past results cannot reliably predict future outcomes. Anyone seeking information about Matka should also be aware that gambling laws vary significantly between countries and states. Before participating in any gambling-related activity, individuals should understand the legal requirements in their jurisdiction and consider the importance of responsible gambling practices.

From a historical perspective, Matka remains an interesting example of how a system linked to commodity market figures evolved into a distinct gambling format. Today, it is frequently discussed in relation to its history, cultural influence, probability concepts, and responsible gambling awareness.` },
  { heading: `Understanding Matka Results and Charts`, body: `Matka results and charts are historical records that document the outcomes of previous draws. Many websites maintain these records to provide users with a chronological archive of past results. Depending on the platform, these charts may be organized by date, game name, or draw session, making it easier for readers to browse historical information.

A Matka chart typically displays past winning numbers in a structured format. People often review these records to understand how results have been presented over time or to compare outcomes from different periods. These charts serve primarily as a historical reference and are intended to preserve publicly available result information.

It is important to recognize that historical results cannot predict future outcomes. Every draw is considered an independent event, meaning previous numbers do not influence the numbers that may appear in subsequent draws. The belief that patterns in historical data can accurately forecast future results is a common misconception and is not supported by probability.

Understanding the difference between historical records and prediction is essential. While Matka charts may be useful for documenting previous outcomes or studying the history of a particular game format, they should not be viewed as reliable forecasting tools. Anyone researching Matka results should approach historical charts as informational records rather than evidence of future trends.

For readers who are interested in gambling-related topics, it is also important to understand the broader context, including the legal status of gambling in their jurisdiction, the financial risks involved, and the importance of responsible gambling practices. Historical data can provide context about past events, but it should never be interpreted as a guarantee or indication of future outcomes.` },
  { heading: `What Is Teen Patti?`, body: `Teen Patti, which translates to &quot;three cards&quot; in Hindi, is a traditional Indian card game that has been enjoyed for generations. It is especially popular during festivals such as Diwali, family celebrations, and social gatherings, where it is often played as a recreational activity among friends and relatives. The game has become an important part of Indian card-playing culture and is widely recognized across the country.

The game is typically played with a standard 52-card deck, with each player receiving three face-down cards. Players compare the strength of their hands based on predetermined hand rankings, and the objective is to have the highest-ranking combination by the end of the round. While the basic concept remains the same, different regions and groups may follow their own variations of the rules and gameplay.

In modern times, Teen Patti has also expanded into digital formats, with numerous online platforms offering virtual versions of the game. Some versions are designed purely for entertainment using virtual chips, while others may involve real-money gambling, depending on the platform and local regulations.

It is important to understand that when Teen Patti is played for money, it becomes a form of gambling and carries financial risks. Outcomes depend on both chance and player decisions, and there is no strategy that can guarantee consistent winnings. Anyone considering playing should first understand the applicable laws in their jurisdiction, be aware of the potential risks, and practice responsible gambling if it is legal where they live.

From a cultural perspective, Teen Patti remains one of India's most recognizable card games, valued not only for its entertainment but also for its long-standing association with social interaction and festive traditions.` },
  { heading: `Online Gambling`, body: `Online gambling refers to gambling activities that are conducted over the internet through websites or mobile applications. These activities can include casino games, sports betting, poker, lottery-style games, bingo, and various number-based gambling formats. Advances in technology have made these platforms widely accessible, allowing users to participate from computers, smartphones, and tablets.

Different online gambling platforms may offer different types of games, features, and payment methods. Some are operated under government-issued licenses in regulated jurisdictions, while others may operate in locations where gambling laws differ. Because regulations vary significantly between countries and even between states or provinces, the availability and legality of online gambling services depend entirely on local laws.

Before using any gambling-related platform, individuals should take time to understand the legal requirements in their jurisdiction. It is also important to verify whether a platform is properly licensed, review its terms and conditions, and understand any applicable age restrictions or consumer protection measures.

Online gambling also involves financial and personal responsibility. Since real money may be involved, participants should recognize that there is always a risk of losing money. Gambling should never be viewed as a reliable source of income or an investment strategy. Setting spending limits, avoiding the pursuit of losses, and treating gambling solely as a form of entertainment are widely recommended responsible gambling practices.

For those researching online gambling from an educational perspective, it is equally important to understand the concepts of probability, randomness, and responsible gambling. Learning about these topics can help individuals make informed decisions while recognizing the potential financial, legal, and psychological risks associated with gambling activities.` },
  { heading: `Important Risks to Consider`, body: `Gambling involves real financial risk and should always be approached with caution. Unlike investments or savings, gambling outcomes are based largely or entirely on chance, and there is no proven strategy that can consistently guarantee winnings over time. While some individuals may experience occasional wins, there is always the possibility of losing money, and losses can accumulate quickly.

Understanding the potential risks is an important part of making informed decisions. Gambling should be viewed as a form of entertainment rather than a way to generate income or solve financial problems. Individuals who choose to gamble should establish clear limits on both time and spending and avoid risking money that is needed for essential living expenses.

Some of the most common risks associated with gambling include:

Financial Losses: Money spent on gambling can be lost entirely, and repeated losses may have a significant impact on personal finances.

Emotional Stress: Winning and losing can create strong emotional reactions, including frustration, anxiety, disappointment, or overconfidence.

Problem Gambling and Addiction: For some individuals, gambling can become difficult to control and may develop into a behavioral addiction that requires professional support.

Debt and Borrowing: Attempting to recover losses by borrowing money or using credit can lead to serious financial difficulties.

Relationship Problems: Excessive gambling may affect trust, communication, and financial stability within families and personal relationships.

Reduced Work or Academic Performance: Spending excessive time gambling may interfere with productivity, concentration, and overall performance at work or school.

Mental Health Impact: Financial pressure and repeated losses may contribute to stress, anxiety, or depression in some individuals.

Anyone who chooses to participate in gambling should first ensure that it is legal in their jurisdiction, understand the risks involved, and never gamble with money they cannot afford to lose.

Common Misconceptions About Gambling

There are many myths surrounding gambling that can create unrealistic expectations. Understanding the facts can help individuals make more informed decisions and avoid common misunderstandings.

### Myth: Previous results can predict future outcomes.

Fact: Independent games of chance do not remember previous results. Historical outcomes do not influence future draws or events, and every new game or draw begins with its own probability.

### Myth: There is a guaranteed winning strategy.

Fact: No betting system, formula, or prediction method can eliminate the mathematical uncertainty that exists in games of chance. While some games may involve elements of skill, no strategy can guarantee consistent profits.

### Myth: Gambling is a reliable way to earn money.

Fact: Gambling should never be considered an investment, career, or dependable source of income. There is always a possibility of losing money, and long-term financial planning should rely on savings, employment, or legitimate investments instead.

### Myth: Chasing losses helps recover money.

Fact: Continuing to gamble in an attempt to recover previous losses often increases financial risk and may lead to even greater losses. Responsible gambling encourages accepting losses and stopping when predetermined limits are reached.

### Myth: Experienced players always have an advantage.

Fact: Experience may help individuals better understand the rules of certain games, but it does not change the role of chance in games where outcomes are random. No level of experience can guarantee success.

By understanding these misconceptions and recognizing the realities of gambling, individuals can make more informed decisions and better appreciate the importance of responsible gambling practices.` },
  { heading: `Responsible Gambling Tips`, body: `If gambling is legal where you live and you choose to participate, it is important to approach it responsibly. Responsible gambling means understanding that gambling is a form of entertainment rather than a reliable way to make money. Setting personal limits and making informed decisions can help reduce the risk of financial and emotional harm.

The following guidelines are commonly recommended to encourage safer gambling habits:

### 1. Set a Spending Limit Before You Begin

Decide in advance how much money you are comfortable spending on gambling and treat that amount as an entertainment expense. Never exceed your predetermined budget, and avoid using money that is needed for essential expenses such as rent, food, education, healthcare, or household bills.

### 2. Decide How Much Time You Will Spend

It is easy to lose track of time while gambling. Setting a time limit before you start can help maintain a healthy balance between gambling and your daily responsibilities. Consider using reminders or alarms to know when it is time to stop.

### 3. Never Chase Losses

One of the most common mistakes is trying to recover money that has already been lost by continuing to gamble. Chasing losses often leads to even greater financial losses and unnecessary stress. Accepting losses as part of gambling is an important aspect of responsible behavior.

### 4. Avoid Gambling During Emotional or Difficult Times

Strong emotions such as stress, frustration, sadness, anger, or excitement can affect decision-making. Similarly, gambling while under the influence of alcohol or drugs may impair judgment and increase the likelihood of making risky decisions. It is generally better to gamble only when you are thinking clearly and can make informed choices.

### 5. Take Regular Breaks

Taking short breaks during gambling sessions can help you stay aware of your spending, reduce impulsive decisions, and maintain perspective. Regular breaks also provide an opportunity to evaluate whether you are still enjoying the activity or whether it is time to stop.

### 6. Keep Gambling Separate From Your Income Goals

Gambling should never be considered a substitute for employment, savings, or investing. There is no guaranteed way to win, and outcomes are unpredictable. It is best to view gambling solely as entertainment and not as a financial strategy.

### 7. Protect Your Personal and Financial Information

If you use online gambling platforms, ensure they are appropriately licensed where applicable, use secure internet connections, and protect your account with strong passwords and two-factor authentication whenever available.

### 8. Seek Help if Gambling Becomes a Problem

If gambling begins to affect your finances, relationships, work, education, or mental well-being, consider seeking support as early as possible. Many countries have confidential organizations, healthcare professionals, and counseling services that specialize in helping people who experience gambling-related harm.

Practicing responsible gambling can help individuals make informed decisions, recognize potential risks, and maintain healthier gambling habits if they choose to participate where it is legal.` },
  { heading: `How Number-Based Gambling Games Work`, body: `Number-based gambling games generally follow a straightforward format in which participants select one or more numbers before an official draw or result is announced. Once the draw is completed, the selected numbers are compared with the published outcome according to the specific rules of that game. The exact structure, number range, draw schedule, and payout system can vary significantly depending on the game, the operator, and the jurisdiction in which it is offered.

Different number-based games may use different methods for generating outcomes, but they all rely on predefined rules that determine how winning numbers are selected and how results are evaluated. Some games involve a single draw, while others may include multiple result sessions throughout the day. Regardless of the format, each draw is treated as a separate event.

One of the most important concepts to understand is that these games are primarily based on chance. Every draw is considered independent, meaning previous outcomes do not affect future results. A number that has appeared recently is not more or less likely to appear again simply because of its past occurrence. This principle of independence is a fundamental concept in probability and applies to random events.

Because outcomes are uncertain, there is no proven method or mathematical formula that can consistently predict future winning numbers. While some people analyze historical records or number patterns, these observations do not guarantee future results. Anyone researching number-based gambling games should understand both the role of probability and the financial risks associated with gambling activities.` },
  { heading: `Why Historical Charts Are Published`, body: `Historical charts are collections of previous results that many websites maintain for informational, archival, and reference purposes. These charts organize past outcomes in a structured format, allowing readers to browse results by date, game name, or draw session. They serve as historical records that document how results have been published over time.

Many people use historical charts to review earlier outcomes, compare different draw periods, or study the history of particular number-based games. For researchers and readers, these records can also provide useful context about how certain games have evolved and how result information has been presented over the years.

However, it is important to understand the purpose of these charts. Historical records are intended to preserve past information—not to predict future outcomes. Since each draw is independent, previous numbers have no influence on future results. Looking for repeating patterns or trends in historical charts does not provide a reliable method for forecasting future winning numbers.

Historical charts should therefore be viewed as informational resources rather than predictive tools. They can help readers understand the history and structure of number-based gambling games, but they should never be interpreted as evidence that future outcomes can be accurately anticipated. Anyone using these records should also keep in mind the importance of responsible gambling and the role of chance in determining results.` },
  { heading: `Understanding Probability`, body: `Probability is a branch of mathematics that measures the likelihood of different outcomes occurring. In games of chance, probability helps explain how likely a particular result is based on the rules of the game. Every eligible outcome has a mathematical chance of being selected, and these probabilities remain consistent unless the rules of the game change.

One of the key concepts in probability is that independent events do not influence one another. In many number-based gambling games, each draw is considered an independent event, meaning previous results have no effect on future outcomes. Whether a specific number appeared in the previous draw or has not appeared for a long time does not change its probability in the next draw.

A common misunderstanding is the belief that a number that has not appeared recently is somehow &quot;due&quot; to appear next. This misconception is known as the gambler's fallacy. In reality, random events have no memory. Every new draw starts with the same mathematical probabilities as any other draw, regardless of what happened previously.

Understanding probability can help individuals develop realistic expectations about games of chance. While historical records and statistical information may be interesting to review, they cannot reliably predict future outcomes. Probability explains why no betting system, prediction method, or pattern analysis can consistently guarantee winning results in independent games of chance.

For anyone researching gambling-related topics, learning the basics of probability is an important step toward understanding randomness, risk, and why responsible gambling practices are strongly recommended.

Legal Considerations

Gambling laws differ significantly around the world and may also vary between states, provinces, or territories within the same country. Some jurisdictions have comprehensive licensing systems that regulate gambling operators, while others restrict or prohibit certain forms of gambling altogether. Because these legal frameworks continue to evolve, the rules that apply in one location may be very different from those in another.

Online gambling is also subject to different regulations depending on local legislation. In some regions, licensed operators are permitted to offer online gambling services under strict regulatory oversight, while in others, access to such services may be restricted or prohibited. Requirements relating to age verification, licensing, taxation, consumer protection, and advertising also vary between jurisdictions.

Before participating in any gambling-related activity, individuals should understand the laws that apply where they live and ensure that any activity complies with local regulations. It is also advisable to verify whether a gambling operator is appropriately licensed and to review any applicable terms, conditions, and responsible gambling policies.

Understanding the legal framework surrounding gambling helps individuals make informed decisions and reduces the risk of unintentionally violating local laws. Regardless of where gambling is legal, it should always be approached responsibly, with an awareness of both the legal requirements and the potential financial and personal risks involved.` },
  { heading: `Financial Responsibility`, body: `Financial responsibility is one of the most important aspects of responsible gambling. Anyone who chooses to gamble where it is legal should treat it strictly as a form of entertainment rather than as a way to earn money or improve their financial situation. Since gambling outcomes are uncertain and based largely on chance, there is always a possibility of losing money.

Establishing healthy financial habits before participating in any gambling activity can help reduce unnecessary financial stress and support better decision-making. Setting clear spending limits and sticking to them is a practical way to maintain control and avoid impulsive behavior.

Some recommended financial responsibility practices include:

Set a Fixed Entertainment Budget: Decide in advance how much money you are comfortable spending and treat it as an entertainment expense rather than an investment.

Never Borrow Money to Gamble: Avoid using loans, credit cards, or borrowed funds for gambling. Gambling with borrowed money can quickly lead to debt and long-term financial difficulties.

Do Not Chase Losses: Attempting to recover previous losses by continuing to gamble often increases financial risk instead of reducing it. Accepting losses and stopping when your budget has been reached is an important responsible gambling habit.

Separate Gambling From Essential Expenses: Money needed for rent, mortgage payments, food, healthcare, education, transportation, or utility bills should never be used for gambling.

Track Your Spending: Keeping a record of gambling-related expenses can help you stay within your budget and better understand your spending habits over time.

Balance Gambling With Other Activities: Gambling should never become the primary focus of your leisure time. Maintaining hobbies, family activities, work responsibilities, and social interactions contributes to a healthier lifestyle.

Practicing financial responsibility helps individuals make informed decisions and reduces the likelihood of gambling causing long-term financial hardship.

Recognizing Warning Signs

Responsible gambling also means recognizing when gambling may be becoming difficult to control. While many people gamble occasionally without experiencing significant problems, others may gradually develop unhealthy gambling habits that affect their finances, relationships, work, education, or overall well-being.

Identifying potential warning signs early makes it easier to seek help before gambling-related issues become more serious.

Some common warning signs include:

Spending More Money Than Planned: Regularly exceeding your gambling budget or spending more than you originally intended.

Gambling More Frequently Over Time: Increasing the amount of time spent gambling or feeling the need to gamble more often.

Feeling Stressed or Anxious About Losses: Frequently worrying about money lost through gambling or feeling pressure to recover those losses.

Hiding Gambling Activity: Keeping gambling habits secret from family members, friends, or colleagues due to embarrassment or concern about their reactions.

Borrowing Money Because of Gambling: Taking loans, using credit, or borrowing from family or friends to continue gambling or repay gambling-related debts.

Difficulty Stopping Despite Wanting To Quit: Feeling unable to reduce or stop gambling even when recognizing that it is causing problems.

Neglecting Personal Responsibilities: Allowing gambling to interfere with work, education, family commitments, or social relationships.

Mood Changes Related to Gambling: Experiencing frustration, irritability, anxiety, or depression as a result of gambling outcomes.

If any of these warning signs begin to appear, seeking support as early as possible is strongly recommended. Many countries provide confidential counseling services, healthcare professionals, and responsible gambling organizations that offer guidance and assistance. Early intervention can help reduce the financial, emotional, and social impact of problem gambling while supporting healthier long-term decision-making.` },
  { heading: `Online Safety`, body: `If you visit gambling-related websites, basic online safety practices remain important:

Protect your personal information.

Use strong, unique passwords.

Enable two-factor authentication where available.

Be cautious of unsolicited messages or offers.

Verify that websites use secure HTTPS connections before sharing personal information.

Matka, Teen Patti, and other gambling activities have cultural and historical significance for many people, but they also involve genuine risks. Understanding how these games work, recognizing common misconceptions, and practicing responsible gambling can help people make more informed decisions.` },
  { heading: `Disclaimer`, body: `This page is provided for informational and educational purposes only. It explains general concepts related to Matka, Teen Patti, and gambling. It should not be interpreted as advice, encouragement, or a recommendation to participate in gambling activities. Anyone considering gambling should first understand the applicable laws in their jurisdiction and the financial risks involved.

### 1. What is Matka?

Matka is a number-based gambling format that originated in India. Over the years, different variations have developed, and the rules may vary depending on the operator and jurisdiction.

### 2. What is a Matka chart?

A Matka chart is a historical record of previous results. It is intended to show past outcomes and should not be viewed as a way to predict future results.

### 3. Can previous Matka results predict future outcomes?

No. Previous results do not determine future outcomes. Independent games of chance are not influenced by earlier draws.

### 4. What is Teen Patti?

Teen Patti is a traditional Indian three-card game that is commonly played socially during festivals and family gatherings. In some settings it may also involve gambling.

### 5. Is Teen Patti the same as poker?

No. Although Teen Patti and poker share some similarities, they have different rules, hand rankings, and gameplay.

### 6. Is online gambling legal?

The legality of online gambling depends on the country, state, or region where you are located. Always check the laws that apply in your jurisdiction.

### 7. Are online gambling games based on skill or chance?

Many gambling games involve chance, while some may include elements of skill. The balance between skill and chance depends on the specific game.

### 8. Can anyone guarantee winning numbers or strategies?

No. There is no guaranteed strategy or system that can reliably predict outcomes or ensure winnings in games of chance.

### 9. Why do people look at historical charts?

Some people review historical charts to understand past outcomes or for record-keeping purposes. However, these records do not provide reliable predictions for future events.

### 10. What is responsible gambling?

Responsible gambling means understanding the risks, setting limits on time and money, and treating gambling as entertainment rather than a way to earn income.

### 11. How can I set a gambling budget?

Decide in advance how much money you are willing to spend for entertainment, and never exceed that amount or use money needed for essential expenses.

### 12. What should I do if gambling is becoming a problem?

If gambling is affecting your finances, relationships, work, or mental well-being, consider seeking help from a qualified healthcare professional or a local gambling support organization.

### 13. What is the gambler's fallacy?

The gambler's fallacy is the mistaken belief that previous random outcomes influence future independent events. In reality, each independent event has its own probability.

### 14. Why is it important to understand probability?

Understanding probability helps explain that games of chance are unpredictable and that outcomes cannot be reliably forecast based on previous results.

### 15. Are gambling websites available everywhere?

No. Availability varies by jurisdiction, and some regions restrict or prohibit gambling websites.

### 16. Can gambling be considered an investment?

No. Gambling should not be viewed as an investment or a reliable source of income because there is always a risk of losing money.

### 17. What are some signs of problem gambling?

Common signs include spending more than intended, chasing losses, borrowing money to gamble, hiding gambling activity, or finding it difficult to stop.

### 18. How can I gamble more responsibly if it is legal where I live?

Set spending and time limits, never chase losses, avoid gambling under emotional distress or while intoxicated, and stop if it is no longer enjoyable.

### 19. How can I stay safe online?

Use strong passwords, enable two-factor authentication when available, protect your personal information, and verify that websites use secure connections before sharing sensitive data.

### 20. Where can I learn more about responsible gambling?

Many countries have independent organizations that provide educational resources and confidential support related to gambling. You can also consult local public health authorities or licensed counseling services for guidance.` },
];

const HOME_SEO_FAQS = [
  { q: "What is Matka?", a: "Matka is a number-based gambling format that originated in India. Over the years, different variations have developed, and the term is now used to describe several types of number-based gambling games." },
  { q: "What is a Matka chart?", a: "A Matka chart is a historical record of previous results. It is intended to show past outcomes and should not be viewed as a tool for predicting future draws." },
  { q: "Can previous Matka results predict future outcomes?", a: "No. Previous results do not determine future outcomes. Independent games of chance are not influenced by earlier draws." },
  { q: "What is Teen Patti?", a: "Teen Patti is a traditional Indian three-card game that is commonly played socially during festivals and family gatherings. When played for money, it is considered a form of gambling." },
  { q: "Is Teen Patti the same as poker?", a: "No. Although Teen Patti and poker share some similarities, they have different rules, hand rankings, and gameplay." },
  { q: "Is online gambling legal?", a: "The legality of online gambling depends on the country, state, or region where you are located. Always check the laws that apply in your jurisdiction before participating." },
  { q: "Can anyone guarantee winning numbers or strategies?", a: "No. There is no guaranteed strategy or system that can reliably predict outcomes or ensure winnings in games of chance." },
  { q: "What is responsible gambling?", a: "Responsible gambling means understanding the risks, setting limits on time and money, and treating gambling as entertainment rather than a source of income." },
  { q: "What should I do if gambling is becoming a problem?", a: "If gambling is affecting your finances, relationships, work, or mental well-being, consider seeking help from a qualified counselor or a national gambling support organization." },
  { q: "What is the gambler's fallacy?", a: "The gambler's fallacy is the mistaken belief that previous random outcomes influence future independent events. In reality, each event is independent and carries the same probability regardless of past results." },
  { q: "Can gambling be considered an investment?", a: "No. Gambling should not be viewed as an investment or a reliable source of income because there is always a risk of losing money." },
  { q: "What are some signs of problem gambling?", a: "Common signs include spending more than intended, chasing losses, borrowing money to gamble, hiding gambling activity, or feeling unable to stop." },
];

export default Home;
